import { useEffect, useMemo, useState } from 'react';
import { orderLedger } from '../lib/api';
import { toast } from 'sonner';
import { BookOpenCheck, TrendingUp, TrendingDown, IndianRupee, Download, Search, RefreshCw, Store as StoreIcon, Loader2, Calendar } from 'lucide-react';

const PAYOUT_STATUS_STYLES = {
  settleable:       'bg-[#E8F5E9] text-[#1B5E20]',
  pending_delivery: 'bg-[#FFF3E0] text-[#9A4500]',
  cancelled:        'bg-muted text-ink/60',
  refunded:         'bg-[#FBE9E7] text-[#BF360C]',
};

export default function AdminLedger() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ payout_status: '', store_id: '', from_date: '', to_date: '', page: 1 });

  const loadSummary = async () => {
    try { setSummary(await orderLedger.summary(days)); } catch { toast.error('Could not load summary'); }
  };

  const loadList = async () => {
    setLoading(true);
    try {
      const params = { page: filters.page, page_size: 25 };
      if (filters.payout_status) params.payout_status = filters.payout_status;
      if (filters.store_id) params.store_id = filters.store_id;
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;
      setList(await orderLedger.list(params));
    } catch { toast.error('Could not load ledger'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadSummary(); /* eslint-disable-next-line */ }, [days]);
  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [filters]);

  const maxDayValue = useMemo(() => {
    if (!summary?.by_day?.length) return 1;
    return Math.max(...summary.by_day.map(d => d.total_order_value || 0)) || 1;
  }, [summary]);

  const csvHref = orderLedger.exportUrl(
    Object.fromEntries(Object.entries(filters).filter(([k, v]) => v && k !== 'page'))
  );

  return (
    <section className="mt-6" data-testid="admin-ledger-section">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-[#0D47A1] text-white grid place-items-center"><BookOpenCheck className="w-6 h-6"/></div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0D47A1]">Accounting</div>
          <h2 className="font-heading font-bold text-2xl">Order Ledger</h2>
          <p className="text-xs text-ink/60 mt-1">One row per order — Total Value, Merchant Payout, Commission Earned. Auto-updated on every payment / delivery / refund. CSV export ready for your CA.</p>
        </div>
        <a data-testid="ledger-download-csv" href={csvHref} className="btn-primary" download>
          <Download className="w-4 h-4"/>Download CSV
        </a>
      </div>

      {/* Summary KPI cards */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          testId="ledger-kpi-total"
          Icon={IndianRupee}
          label="Total Order Value"
          value={summary?.totals?.total_order_value}
          sub={`${summary?.totals?.orders ?? 0} orders in the last ${days} days`}
          tone="blue"
        />
        <KpiCard
          testId="ledger-kpi-payout"
          Icon={StoreIcon}
          label="Merchant Payout"
          value={summary?.totals?.merchant_payout}
          sub="Amount owed to shopkeepers"
          tone="orange"
        />
        <KpiCard
          testId="ledger-kpi-commission"
          Icon={TrendingUp}
          label="Commission Earned"
          value={summary?.totals?.commission_earned}
          sub="Gross platform revenue"
          tone="green"
        />
        <KpiCard
          testId="ledger-kpi-net"
          Icon={TrendingDown}
          label="Net Platform Revenue"
          value={summary?.totals?.net_platform_revenue}
          sub="After promos & MandiCoins"
          tone="violet"
        />
      </div>

      {/* Daily chart */}
      <div className="mt-6 card-soft p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="font-heading font-bold text-lg inline-flex items-center gap-2"><Calendar className="w-5 h-5"/>Daily order value</div>
          <div className="flex items-center gap-2">
            {[7, 14, 30, 90].map(d => (
              <button
                key={d}
                data-testid={`ledger-days-${d}`}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 text-xs rounded-full border ${days === d ? 'bg-[#0D47A1] text-white border-[#0D47A1]' : 'bg-white text-ink/70 border-border hover:border-[#0D47A1]/40'}`}
              >{d}d</button>
            ))}
            <button data-testid="ledger-refresh-summary" onClick={loadSummary} className="p-1.5 rounded-full border border-border hover:border-[#0D47A1]/40" title="Refresh"><RefreshCw className="w-3.5 h-3.5"/></button>
          </div>
        </div>
        {!summary ? (
          <div className="text-ink/50 text-sm inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Crunching numbers…</div>
        ) : summary.by_day.length === 0 ? (
          <div className="text-ink/50 text-sm">No data in the last {days} days.</div>
        ) : (
          <div className="flex items-end gap-1 h-40 overflow-x-auto">
            {summary.by_day.map(d => {
              const h = Math.max(4, Math.round((d.total_order_value / maxDayValue) * 140));
              return (
                <div key={d.date} data-testid={`ledger-bar-${d.date}`} className="group flex flex-col items-center min-w-[24px] cursor-pointer" title={`${d.date}\n₹${d.total_order_value.toFixed(2)} · ${d.orders} orders`}>
                  <div
                    className="w-4 bg-gradient-to-t from-[#0D47A1] to-[#42A5F5] rounded-t group-hover:from-[#D35400] group-hover:to-[#FB8C00] transition-colors"
                    style={{ height: `${h}px` }}
                  />
                  <div className="mt-1 text-[9px] text-ink/40 rotate-45 origin-left whitespace-nowrap">{d.date.slice(5)}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="mt-6 card-soft p-4 grid md:grid-cols-12 gap-2 items-end">
        <div className="md:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 mb-1">Payout status</div>
          <select data-testid="ledger-filter-status" value={filters.payout_status} onChange={e => setFilters({ ...filters, payout_status: e.target.value, page: 1 })} className="w-full border border-border bg-white rounded-lg px-3 py-2 text-sm">
            <option value="">Any</option>
            <option value="pending_delivery">Pending delivery</option>
            <option value="settleable">Settleable</option>
            <option value="cancelled">Cancelled</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
        <div className="md:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 mb-1">Store ID</div>
          <input data-testid="ledger-filter-store" value={filters.store_id} onChange={e => setFilters({ ...filters, store_id: e.target.value, page: 1 })} placeholder="filter by store id" className="w-full border border-border bg-white rounded-lg px-3 py-2 text-sm"/>
        </div>
        <div className="md:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 mb-1">From</div>
          <input data-testid="ledger-filter-from" type="date" value={filters.from_date} onChange={e => setFilters({ ...filters, from_date: e.target.value, page: 1 })} className="w-full border border-border bg-white rounded-lg px-3 py-2 text-sm"/>
        </div>
        <div className="md:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 mb-1">To</div>
          <input data-testid="ledger-filter-to" type="date" value={filters.to_date} onChange={e => setFilters({ ...filters, to_date: e.target.value, page: 1 })} className="w-full border border-border bg-white rounded-lg px-3 py-2 text-sm"/>
        </div>
        <div className="md:col-span-2">
          <button data-testid="ledger-clear-filters" onClick={() => setFilters({ payout_status: '', store_id: '', from_date: '', to_date: '', page: 1 })} className="btn-outline w-full !py-2">Clear filters</button>
        </div>
      </div>

      {/* Table */}
      <div className="mt-4 card-soft overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-ink/50 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div>
        ) : !list?.rows?.length ? (
          <div className="p-10 text-center text-ink/60">
            <BookOpenCheck className="w-10 h-10 mx-auto text-ink/30"/>
            <div className="font-heading font-bold text-base mt-3">No ledger rows for these filters</div>
          </div>
        ) : (
          <>
            <div className="px-5 py-3 border-b border-border text-xs text-ink/60">
              Showing <b>{list.rows.length}</b> of <b>{list.total}</b> orders ·
              {' '}Filtered totals: <b>₹{(list.totals.total_order_value || 0).toLocaleString('en-IN')}</b> value ·
              <b> ₹{(list.totals.merchant_payout || 0).toLocaleString('en-IN')}</b> payout ·
              <b> ₹{(list.totals.commission_earned || 0).toLocaleString('en-IN')}</b> commission
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-ink/50 border-b border-border">
                  <th className="py-2 px-3">Order</th>
                  <th className="py-2 px-3">Store</th>
                  <th className="py-2 px-3">When</th>
                  <th className="py-2 px-3">Method</th>
                  <th className="py-2 px-3 text-right">Total ₹</th>
                  <th className="py-2 px-3 text-right">Payout ₹</th>
                  <th className="py-2 px-3 text-right">Commission ₹</th>
                  <th className="py-2 px-3 text-right">GST ₹</th>
                  <th className="py-2 px-3">Payout status</th>
                </tr>
              </thead>
              <tbody>
                {list.rows.map(r => (
                  <tr key={r.order_id} data-testid={`ledger-row-${r.order_id}`} className="border-b border-border/40 hover:bg-[#FFF8E1]/40">
                    <td className="py-2 px-3 font-mono text-[11px]">#{r.order_id.slice(0, 8)}</td>
                    <td className="py-2 px-3">
                      <div className="font-semibold">{r.store_name || '—'}</div>
                      <div className="text-[10px] text-ink/40">{r.category}</div>
                    </td>
                    <td className="py-2 px-3 text-[11px] text-ink/60">{(r.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                    <td className="py-2 px-3 text-[11px]"><span className="pill !text-[9px] !bg-[#E3F2FD] !text-[#0D47A1]">{r.payment_method || '—'}</span></td>
                    <td className="py-2 px-3 text-right tabular-nums font-semibold">₹{(r.total_order_value || 0).toFixed(2)}</td>
                    <td className="py-2 px-3 text-right tabular-nums text-[#9A4500] font-semibold">₹{(r.merchant_payout || 0).toFixed(2)}</td>
                    <td className="py-2 px-3 text-right tabular-nums text-[#1B5E20] font-semibold">₹{(r.commission_earned || 0).toFixed(2)}</td>
                    <td className="py-2 px-3 text-right tabular-nums text-ink/70">₹{((r.commission_gst || 0) + (r.item_gst_collected || 0)).toFixed(2)}</td>
                    <td className="py-2 px-3"><span className={`pill !text-[9px] capitalize ${PAYOUT_STATUS_STYLES[r.payout_status] || ''}`}>{(r.payout_status || '').replace(/_/g, ' ')}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {list.total > list.page_size && (
              <div className="px-5 py-3 flex items-center justify-between text-xs">
                <span className="text-ink/60">Page {list.page} of {Math.ceil(list.total / list.page_size)}</span>
                <div className="flex gap-2">
                  <button data-testid="ledger-prev-page" disabled={list.page <= 1} onClick={() => setFilters({ ...filters, page: filters.page - 1 })} className="btn-outline !py-1.5 disabled:opacity-40">Prev</button>
                  <button data-testid="ledger-next-page" disabled={list.page >= Math.ceil(list.total / list.page_size)} onClick={() => setFilters({ ...filters, page: filters.page + 1 })} className="btn-outline !py-1.5 disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function KpiCard({ testId, Icon, label, value, sub, tone }) {
  const styles = {
    blue:   'bg-[#E3F2FD] border-[#90CAF9] text-[#0D47A1]',
    orange: 'bg-[#FFF3E0] border-[#FFCC80] text-[#9A4500]',
    green:  'bg-[#E8F5E9] border-[#A5D6A7] text-[#1B5E20]',
    violet: 'bg-[#EDE7F6] border-[#B39DDB] text-[#4527A0]',
  }[tone] || 'bg-white border-border text-ink';
  return (
    <div data-testid={testId} className={`rounded-2xl border-2 p-4 ${styles}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">{label}</div>
        <Icon className="w-4 h-4 opacity-70"/>
      </div>
      <div className="font-heading font-black text-2xl mt-2 tabular-nums">
        ₹{(value ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
      </div>
      <div className="text-[11px] opacity-70 mt-1 leading-snug">{sub}</div>
    </div>
  );
}
